import { useState, useEffect, useRef } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import HomePage from "./pages/HomePage";
import ScanPage from "./pages/ScanPage";
import CardDetailPage from "./pages/CardDetailPage";
import CollectionPage from "./pages/CollectionPage";
import ProfilePage from "./pages/ProfilePage";
import PricingGuidePage from "./pages/PricingGuidePage";
import RarityGuidePage from "./pages/RarityGuidePage";
import CollectorTipsPage from "./pages/CollectorTipsPage";
import FAQPage from "./pages/FAQPage";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import NotFound from "./pages/NotFound";
import Index from "./pages/Index";
import { checkSubscriptionRedirect } from "./lib/payment";
import { cameraService } from "./services/cameraService";
import { scanQuotaService } from "./services/scanQuotaService";
import { auth } from "./lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useScanQuota } from "./hooks/useScanQuota";
import { SuccessCelebration } from "@/components/features/scanner/SuccessCelebration";

// Set up title
document.title = "PokeScan - Pokémon Card Value Scanner";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 30000,
      refetchOnWindowFocus: false
    }
  }
});

// ScrollToTop component to handle scrolling on route changes
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth"
    });
  }, [pathname]);
  
  return null;
}

function AppRoutes() {
  const location = useLocation();
  const [showCelebration, setShowCelebration] = useState(false);
  const authChangeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastAuthChangeRef = useRef<number>(0);
  const MIN_AUTH_REFRESH_INTERVAL = 5000; // 5 seconds - increased to prevent loops
  
  // Use the improved useScanQuota hook
  const { quota, refreshQuota } = useScanQuota(120000); // Refresh every 2 minutes
  
  // Clean up camera resources when navigating away from scan page
  useEffect(() => {
    const isScanPage = location.pathname.includes('/scan');
    
    if (!isScanPage) {
      // Reset camera service when navigating away from scan page
      console.log("Navigating away from scanner, resetting camera service");
      cameraService.reset(); // Use the reset method to properly clean up resources
    }
    
    // Always clean up when component unmounts or before unload
    const handleBeforeUnload = () => {
      cameraService.reset();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location.pathname]);
  
  // Set up auth state change listener with debouncing to prevent refresh loops
  useEffect(() => {
    const handleAuthChange = (user: any) => {
      const now = Date.now();
      
      // Skip if auth changed too recently to prevent loops
      if (now - lastAuthChangeRef.current < MIN_AUTH_REFRESH_INTERVAL) {
        console.log('Auth change handled too recently, skipping');
        return;
      }
      
      console.log('Auth state changed in App:', user ? 'logged in' : 'logged out');
      lastAuthChangeRef.current = now;
      
      // Invalidate scan quota cache on auth state change
      scanQuotaService.invalidateCache();
      
      // Clear any pending auth change timer
      if (authChangeTimerRef.current) {
        clearTimeout(authChangeTimerRef.current);
      }
      
      // Add a larger delay to ensure auth state is fully processed
      // and prevent multiple rapid refreshes
      authChangeTimerRef.current = setTimeout(() => {
        console.log('Refreshing quota after auth change');
        authChangeTimerRef.current = null;
        refreshQuota();
      }, 1500);
    };
    
    const unsubscribe = onAuthStateChanged(auth, handleAuthChange);
    
    return () => {
      unsubscribe();
      if (authChangeTimerRef.current) {
        clearTimeout(authChangeTimerRef.current);
        authChangeTimerRef.current = null;
      }
    };
  }, [refreshQuota]);
  
  // FIXED: Don't show celebration here when on Profile page to avoid duplicates
  // Only check for subscription payment redirects on non-Profile pages
  useEffect(() => {
    // Skip subscription redirect check on Profile page to avoid duplicates
    if (location.pathname.includes('/profile')) {
      return; 
    }
    
    const checkAndHandleRedirect = async () => {
      try {
        // Check if this is a redirect from a subscription payment
        const wasPaymentRedirect = await checkSubscriptionRedirect();
        
        // If it was a successful subscription, show the celebration
        if (wasPaymentRedirect && location.search.includes("subscription=success")) {
          console.log("Showing celebration after successful payment");
          
          // Invalidate quota cache as subscription status changed
          scanQuotaService.invalidateCache();
          
          // Refresh quota to get latest scan count
          await refreshQuota();
          
          // Small delay to ensure the URL is cleaned and the UI is ready
          setTimeout(() => {
            setShowCelebration(true);
          }, 1000);
        }
      } catch (error) {
        console.error("Error handling subscription redirect:", error);
      }
    };
    
    checkAndHandleRedirect();
  }, [location, refreshQuota]);

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/scan" element={<ScanPage />} />
        <Route path="/card/:id" element={<CardDetailPage />} />
        <Route path="/collection" element={<CollectionPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/pricing-guide" element={<PricingGuidePage />} />
        <Route path="/rarity-guide" element={<RarityGuidePage />} />
        <Route path="/collector-tips" element={<CollectorTipsPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      
      <AnimatePresence>
        {showCelebration && quota && (
          <SuccessCelebration 
            onClose={() => setShowCelebration(false)}
            scanCount={quota.remainingScans || 0}
            message="Subscription Successful! Welcome to PokeScan!"
            subscriptionTier={quota.subscriptionTier as 'basic' | 'pro' | null}
          />
        )}
      </AnimatePresence>
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
